import React, { useState, useRef, useEffect } from 'react';
import { IonButton, IonIcon, IonSpinner } from '@ionic/react';
import hamIcon from '../../assets/ham-icon.svg';
import { PopoverMenuComponent } from '../ui/PopoverMenuComponent';
import { Course } from '../../types/course';
import {
  UserCourse,
  LessonType,
  LessonResponse,
  GameProgress,
  updateGameLesson,
  calculateCourseProgress,
} from '../../types/userCourse';
import {
  Game,
  GameScenario as APIGameScenario,
  NormalizedScenario,
  ProspectProfile,
  FinalResults,
} from '../../types/lessonGame';
import { courseService } from '../../services/courseService';
import { userCourseService } from '../../services/userCourseService';
import { showError } from '../../services/toastService';
import { useProfileStore } from '../../store/useProfileStore';
import { progressService } from '../../services/progressService';
import { badgeModalService } from '../../services/badgeModalService';
import { chevronBackOutline, chevronForwardOutline } from 'ionicons/icons';

// ============================================================================
// TYPES & MODELS
// ============================================================================

interface LessonGameProps {
  course: Course;
  userCourse: UserCourse | null;
  onProgressUpdate?: (userCourse: UserCourse) => void;
  progressToNext?: () => void;
  onToggleSidebar?: () => void;
  hasNextLesson: boolean;
  hasPreviousLesson: boolean;
  onNavigateToNext?: () => void;
  onNavigateToPrevious?: () => void;
  stepNumber: number;
  totalSteps: number;
  /** When on last lesson and next is next course: "Next Course" if there is a next course, "Return" if not */
  nextStepLabel?: 'Next Course' | 'Return';
}

// ============================================================================
// COMPONENT
// ============================================================================

export const LessonGame: React.FC<LessonGameProps> = ({
  course,
  userCourse,
  onProgressUpdate,
  progressToNext,
  onToggleSidebar,
  hasNextLesson,
  hasPreviousLesson,
  onNavigateToNext,
  onNavigateToPrevious,
  stepNumber,
  totalSteps,
  nextStepLabel = 'Next Course'
}) => {
  const sessionStartRef = useRef<Date>(new Date());

  // ============================================================================
  // STATE MANAGEMENT
  // ============================================================================
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [game, setGame] = useState<Game | null>(null);
  const [scenarios, setScenarios] = useState<NormalizedScenario[]>([]);
  const [prospectProfile, setProspectProfile] = useState<ProspectProfile | null>(null);
  const [finalResults, setFinalResults] = useState<FinalResults | null>(null);

  const [gameStatus, setGameStatus] = useState<'intro' | 'playing' | 'results'>('intro');
  const [currentScenarioIndex, setCurrentScenarioIndex] = useState(0);
  const [score, setScore] = useState(0);
  const [selectedChoiceIndex, setSelectedChoiceIndex] = useState<number | null>(null);
  const [showFeedback, setShowFeedback] = useState(false);
  const [scenarioResponses, setScenarioResponses] = useState<number[]>([]);
  const [lastSavedResponses, setLastSavedResponses] = useState<number[]>([]);
  const [showNavigationPopover, setShowNavigationPopover] = useState(false);
  const [navigationPopoverEvent, setNavigationPopoverEvent] = useState<Event | undefined>(undefined);
  const [navigationDirection, setNavigationDirection] = useState<'next' | 'previous' | null>(null);
  const saveInProgressRef = useRef(false);
  const pendingSaveRef = useRef<{ responses: number[] } | null>(null);
  const hasCheckedBadgesRef = useRef<boolean>(false);
  const skipResumeOnNextUserCourseRef = useRef(false);
  
  const profile = useProfileStore((state) => state.activeProfile);

  const currentScenario = scenarios[currentScenarioIndex];
  const totalScenarios = scenarios.length;

  // Calculate interest level (0-100) for the Interest Meter
  const interestLevel = Math.max(0, Math.min(100, 50 + score));

  // Calculate current progress
  const currentProgress = React.useMemo(() => {
    // Check if lesson is completed from saved data
    const lesson = userCourse?.userLessons.find(
      l => l.lessonId === course.gameId && l.lessonType === LessonType.GAME
    );

    // If completed, always show 100%
    if (lesson?.completedAt) {
      return { percentComplete: 100, isComplete: true };
    }

    const isComplete = scenarioResponses.length === totalScenarios;
    const percentComplete = totalScenarios > 0
      ? Math.round((scenarioResponses.length / totalScenarios) * 100)
      : 0;
    return {
      percentComplete,
      isComplete
    };
  }, [scenarioResponses.length, totalScenarios, userCourse, course.gameId]);

  // Determine lesson status based on userCourse data
  const lessonStatus = React.useMemo(() => {
    if (!userCourse) return 'Not Started';

    const lesson = userCourse.userLessons.find(
      l => l.lessonId === course.gameId && l.lessonType === LessonType.GAME
    );

    if (!lesson || !lesson.startedAt) return 'Not Started';
    if (lesson.completedAt) return 'Completed';
    return 'In Progress';
  }, [userCourse, course.gameId]);

  // Get status styling
  const getStatusStyle = () => {
    switch (lessonStatus) {
      case 'Completed':
        return 'bg-green-500 text-white';
      case 'In Progress':
        return 'bg-red-500 text-white';
      case 'Not Started':
        return 'bg-medium';
      default:
        return 'bg-medium';
    }
  };

  // Handle navigation with confirmation if lesson is in progress
  const handleNavigationClick = (direction: 'next' | 'previous', event: React.MouseEvent) => {
    if (lessonStatus === 'In Progress') {
      setNavigationDirection(direction);
      setNavigationPopoverEvent(event.nativeEvent);
      setShowNavigationPopover(true);
    } else {
      // If not in progress, navigate directly
      if (direction === 'next') {
        onNavigateToNext?.();
      } else {
        onNavigateToPrevious?.();
      }
    }
  };

  const handlePopoverDismiss = (selectedKey?: string) => {
    setShowNavigationPopover(false);
    setNavigationPopoverEvent(undefined);

    if (selectedKey === 'confirm') {
      // User confirmed navigation
      if (navigationDirection === 'next') {
        onNavigateToNext?.();
      } else if (navigationDirection === 'previous') {
        onNavigateToPrevious?.();
      }
    }
    setNavigationDirection(null);
  };

  // ============================================================================
  // DATA FETCHING & NORMALIZATION
  // ============================================================================

  useEffect(() => {
    const fetchGame = async () => {
      if (!course.id) return;

      try {
        setLoading(true);
        const gameData = await courseService.getGame(course.id);

        // Normalize scenarios from API format to internal format
        const qualityLevels = ['Excellent', 'Good', 'Poor', 'Dangerous'] as const;
        const normalizeQualityLevel = (raw: string): (typeof qualityLevels)[number] => {
          const lower = (raw || '').trim().toLowerCase();
          const found = qualityLevels.find(q => q.toLowerCase() === lower);
          return found ?? 'Poor';
        };

        const normalizedScenarios: NormalizedScenario[] = gameData.scenarios.map((scenario: APIGameScenario) => ({
          scenarioNumber: scenario.scenario_number,
          title: scenario.title,
          type: scenario.type,
          scenarioText: scenario.scenario_text,
          choices: scenario.choices.map(choice => ({
            letter: choice.letter,
            text: choice.choice_text,
            qualityLevel: normalizeQualityLevel(choice.quality_level),
            impactScore: choice.impact_score,
            feedbackTitle: choice.feedback_title,
            feedbackText: choice.feedback_text,
          }))
        }));

        setGame(gameData);
        setScenarios(normalizedScenarios);
        setProspectProfile(gameData.prospectProfile);
        setFinalResults(gameData.finalResults);
        setLoading(false);
      } catch (err) {
        // Error modal presented by API
        setError('Failed to load game data');
        setLoading(false);
      }
    };

    fetchGame();
  }, [course.id]);

  // Resume from saved progress (skip when update came from our own save during active play)
  useEffect(() => {
    if (!userCourse || !course.gameId || scenarios.length === 0) return;

    if (skipResumeOnNextUserCourseRef.current) {
      skipResumeOnNextUserCourseRef.current = false;
      return;
    }

    const lesson = userCourse.userLessons.find(
      l => l.lessonId === course.gameId && l.lessonType === LessonType.GAME
    );

    // Only resume if lesson exists and is not completed
    if (lesson && !lesson.completedAt) {
      // Restore scenario index from lesson progress
      if (lesson.currentPositionIndex > 0) {
        setCurrentScenarioIndex(lesson.currentPositionIndex);
        setGameStatus('playing'); // Resume in playing state, not intro
      }

      // Restore responses from lessonResponses if they exist
      if (userCourse.lessonResponses && userCourse.lessonResponses.length > 0) {
        const gameResponses = userCourse.lessonResponses
          .filter(r => r.lessonId === course.gameId && r.lessonType === LessonType.GAME)
          .sort((a, b) => a.itemNumber - b.itemNumber);

        if (gameResponses.length > 0) {
          // Convert letter responses back to choice indices
          const restoredResponses: number[] = [];
          gameResponses.forEach(response => {
            // itemNumber is 1-based, convert to 0-based index
            const scenarioIdx = response.itemNumber - 1;
            const scenario = scenarios[scenarioIdx];

            if (scenario) {
              // Find the choice index that matches the saved letter
              const choiceIdx = scenario.choices.findIndex(c => c.letter === response.response);
              if (choiceIdx !== -1) {
                restoredResponses.push(choiceIdx);
              }
            }
          });

          setScenarioResponses(restoredResponses);
          setLastSavedResponses(restoredResponses);
        }
      }
    }
  }, [userCourse, course.gameId, scenarios]);

  // ============================================================================
  // PROGRESS TRACKING
  // ============================================================================

  // Save progress to UserCourse - accepts responses as parameter to avoid stale closure
  const saveProgressWithResponses = async (responsesToSave: number[]) => {
    if (!course.gameId) return;

    // If a save is in progress, queue this save for later
    if (saveInProgressRef.current) {
      pendingSaveRef.current = { responses: responsesToSave };
      return;
    }

    saveInProgressRef.current = true;

    try {
      // Count excellent/good choices
      let excellentGoodCount = 0;
      responsesToSave.forEach((choiceIdx, scenarioIdx) => {
        const scenario = scenarios[scenarioIdx];
        const choice = scenario?.choices[choiceIdx];
        if (choice && (choice.qualityLevel === 'Excellent' || choice.qualityLevel === 'Good')) {
          excellentGoodCount++;
        }
      });

      const isCompleted = responsesToSave.length === totalScenarios;

      // Current position index should be the next unanswered scenario
      // If user has answered 3 scenarios (indices 0,1,2), they should resume at index 3
      const nextScenarioIndex = responsesToSave.length;

      const gameProgress: GameProgress = {
        gameId: course.gameId,
        currentScenarioIndex: nextScenarioIndex,
        totalResponses: responsesToSave.length,
        correctResponses: excellentGoodCount,
        score: score,
        percentComplete: isCompleted ? 100 : Math.round((responsesToSave.length / totalScenarios) * 100),
        isComplete: isCompleted,
        attempt: 1,
        lastAttempted: new Date().toISOString()
      };

      // Find existing game lesson
      const existingLesson = userCourse?.userLessons.find(
        lesson => lesson.lessonId === course.gameId && lesson.lessonType === LessonType.GAME
      );

      // Update lesson with new progress
      const updatedLesson = updateGameLesson(
        existingLesson,
        gameProgress,
        course.gameId,
        totalScenarios,
        sessionStartRef.current
      );

      if (!userCourse) {
        showError('Cannot save video progress - user course data not loaded');
        return;
      }

      // Create LessonResponse objects for newly answered scenarios (not yet saved)
      const newResponses: LessonResponse[] = [];

      // Only save responses that haven't been saved before
      responsesToSave.forEach((choiceIdx, scenarioIdx) => {
        // Check if this scenario response was already saved
        if (scenarioIdx >= lastSavedResponses.length || lastSavedResponses[scenarioIdx] !== choiceIdx) {
          const scenario = scenarios[scenarioIdx];
          const choice = scenario?.choices[choiceIdx];

          if (choice) {
            const isCorrect = choice.qualityLevel === 'Excellent' || choice.qualityLevel === 'Good';

            const response: LessonResponse = {
              lessonId: course.gameId,
              lessonType: LessonType.GAME,
              itemNumber: scenarioIdx + 1, // Scenario number (1-based)
              correctResponse: isCorrect,
              responseAt: new Date().toISOString(),
              response: choice.letter,
              attempt: gameProgress.attempt,
              impactScore: choice.impactScore
            };

            newResponses.push(response);
          }
        }
      });

      // Update lastSavedResponses to current state
      setLastSavedResponses([...responsesToSave]);

      // Update UserCourse with modified lesson
      const updatedLessons = existingLesson
        ? userCourse.userLessons.map(l =>
            l.lessonId === course.gameId && l.lessonType === LessonType.GAME
              ? updatedLesson
              : l
          )
        : [...userCourse.userLessons, updatedLesson];

      // Calculate overall course progress and lessons completed
      const { progress, lessonsCompleted } = calculateCourseProgress(
        updatedLessons,
        course.steps
      );

      const updatedUserCourse: UserCourse = {
        ...userCourse,
        userLessons: updatedLessons,
        progress,
        lessonsCompleted,
        lastAccessedAt: new Date().toISOString(),
        lastAccessedLesson: LessonType.GAME,
        lastAccessedLessonId: course.gameId,
        lessonResponses: [...(userCourse.lessonResponses || []), ...newResponses]
      };

      await userCourseService.updateUserCourse(updatedUserCourse);

      skipResumeOnNextUserCourseRef.current = true;
      onProgressUpdate?.(updatedUserCourse);

      // Check for newly earned badges if course is completed (100% progress)
      // Only check once per completion to avoid duplicate checks
      if (progress >= 100 && !hasCheckedBadgesRef.current && profile?.id) {
        hasCheckedBadgesRef.current = true;
        // Fire and forget - fetch summary and check for new badges
        progressService.getTeamUserSummary(profile.id)
          .then(summary => {
            if (summary.badges) {
              badgeModalService.checkAndShowNewBadges(summary.badges, profile.id);
            }
          })
          .catch(err => {
            // Silent failure - don't interrupt user experience
            console.error('Failed to check for badges after game completion:', err);
          });
      }
    } finally {
      saveInProgressRef.current = false;

      // If there's a pending save, process it now
      if (pendingSaveRef.current) {
        const pending = pendingSaveRef.current;
        pendingSaveRef.current = null;
        // Schedule on next tick to avoid recursion depth issues
        setTimeout(() => {
          saveProgressWithResponses(pending.responses);
        }, 0);
      }
    }
  };

  // ============================================================================
  // HANDLERS
  // ============================================================================

  const handleStartGame = () => {
    setGameStatus('playing');
    setCurrentScenarioIndex(0);
    setScore(0);
    setScenarioResponses([]);
    sessionStartRef.current = new Date();

    // Save initial progress to mark lesson as started and begin tracking duration
    setTimeout(() => saveProgressWithResponses([]), 0);
  };

  const handleSelectChoice = (index: number) => {
    if (showFeedback) return;

    setSelectedChoiceIndex(index);
    setShowFeedback(true);

    // Update score immediately
    const choice = currentScenario.choices[index];
    setScore(prev => prev + choice.impactScore);

    // Track this scenario as answered
    const newResponses = [...scenarioResponses, index];
    setScenarioResponses(newResponses);

    // Save progress immediately after choice is made (pass new responses to avoid stale closure)
    setTimeout(() => saveProgressWithResponses(newResponses), 0);
  };

  const handleNextScenario = () => {
    // Save progress in background (non-blocking)
    saveProgressWithResponses(scenarioResponses);

    setShowFeedback(false);
    setSelectedChoiceIndex(null);

    if (currentScenarioIndex < scenarios.length - 1) {
      setCurrentScenarioIndex(prev => prev + 1);
    } else {
      setGameStatus('results');
      // Reset badge check flag when game completes
      hasCheckedBadgesRef.current = false;
      // Note: progressToNext() is called when user clicks "Next Lesson" button on results screen
    }
  };

  // ============================================================================
  // LOADING & ERROR STATES
  // ============================================================================

  if (loading) {
    return (
      <div className="h-full flex flex-col overflow-hidden">
        <div className="flex-shrink-0 pt-4 px-8">
          <div className="flex items-center gap-4 mb-4">
            <button
              onClick={onToggleSidebar}
              className="lg:hidden flex items-center justify-center w-10 h-10 hover:bg-gray-100 rounded transition-colors"
              aria-label="Toggle menu"
            >
              <IonIcon icon={hamIcon} size="small" color="medium" />
            </button>
            <h1 className="text-xl font-bold">{course.gameTitle}</h1>
          </div>
        </div>
        <div className="flex-1 flex items-center justify-center">
          <IonSpinner name="crescent" />
        </div>
      </div>
    );
  }

  if (error || !game) {
    return (
      <div className="h-full flex flex-col overflow-hidden">
        <div className="flex-shrink-0 pt-4 px-8">
          <div className="flex items-center gap-4 mb-4">
            <button
              onClick={onToggleSidebar}
              className="lg:hidden flex items-center justify-center w-10 h-10 hover:bg-gray-100 rounded transition-colors"
              aria-label="Toggle menu"
            >
              <IonIcon icon={hamIcon} size="small" color="medium" />
            </button>
            <h1 className="text-xl font-bold">{course.gameTitle}</h1>
          </div>
        </div>
        <div className="flex-1 flex items-center justify-center">
          <p className="text-red-500">{error || 'Failed to load game'}</p>
        </div>
      </div>
    );
  }

  // ============================================================================
  // RENDER: INTRO SCREEN
  // ============================================================================

  if (gameStatus === 'intro') {
    return (
      <div className="h-full flex flex-col overflow-hidden">
        {/* Fixed Header */}
        <div className="flex-shrink-0 pt-4 px-4 sm:px-8">
          {/* Game Title with Hamburger Menu */}
          <div className="flex items-center gap-4 mb-2">
            <button
              onClick={onToggleSidebar}
              className="lg:hidden flex items-center justify-center w-10 h-10 hover:bg-gray-100 rounded transition-colors"
              aria-label="Toggle menu"
            >
              <IonIcon icon={hamIcon} size="small" color="medium" />
            </button>
            <h1 className="text-xl sm:text-2xl font-bold">{course.gameTitle}</h1>
          </div>
        </div>

        {/* Scrollable Content */}
        <div className="flex-1 overflow-y-auto px-8 pb-8">
 
          <div id="lesson-progress">
            {/* Lesson Progress Indicator */}
            <div className="mb-3 flex items-center justify-between">
              <div>
                <span className="text-sm font-normal text-gray-600">STEP {stepNumber} OF {totalSteps}</span>
              </div>
              <div className="flex items-center">
                <div className={`mx-2 px-2 py-1.5 mt-1 text-xs font-medium rounded ${getStatusStyle()}`}>
                  {lessonStatus}
                </div>
                <div className="flex gap-0.5">
                  <IonButton
                    className="toolbar-button-icon-small"
                    fill="solid"
                    size="small"
                    color="medium"
                    onClick={(e) => handleNavigationClick('previous', e)}
                  >
                    <IonIcon icon={chevronBackOutline} className="fs-16" color="secondary" />
                  </IonButton>
                  <IonButton
                    className="toolbar-button-icon-small"
                    fill="solid"
                    size="small"
                    color="medium"
                    disabled={!hasNextLesson}
                    onClick={(e) => handleNavigationClick('next', e)}
                  >
                    <IonIcon icon={chevronForwardOutline} className="fs-16" color="secondary" />
                  </IonButton>
                </div>
              </div>
            </div>

            {/* Progress Indicator */}
            {currentProgress && currentProgress.percentComplete < 100 && (
              <div className="mb-4 p-3 bg-gray-100 rounded-lg">
                <div className="flex justify-between items-center">
                  <div className="text-sm font-medium text-gray-700">
                    <span className="hidden sm:inline">Progress: </span>{Math.round(currentProgress.percentComplete)}%
                  </div>
                  <div className="grow mx-4 h-2 bg-medium rounded-full overflow-hidden">
                    <div
                      className="w-full h-full bg-success rounded-full transition-all duration-300 ease-in-out"
                      style={{ width: `${Math.min(Math.max(Math.round(currentProgress.percentComplete), 0), 100)}%` }}
                    />
                  </div>
                </div>
              </div>
            )}
          </div>

          <div className="max-w-5xl mx-auto">
            {/* Banner Image */}
            <div className="w-full h-32 bg-gradient-to-r from-blue-200 to-indigo-100 rounded-t-2xl relative overflow-hidden mb-6">
              <img src={game.imageUrl} alt="Header" className="object-cover w-full h-full opacity-80" />
            </div>

            <div className="bg-gray-50 border border-gray-200 rounded-2xl p-8 md:p-12 text-center shadow-sm">
              <h2 className="text-2xl font-bold mb-4">{game.header}</h2>
              <div 
                className="text-gray-800 max-w-2xl mx-auto mb-8 text-lg"
                dangerouslySetInnerHTML={{ __html: prospectProfile?.background || '' }}
              />

              <div className="text-left max-w-md mx-auto mb-10 grid grid-cols-1 sm:grid-cols-2 gap-4">
                {game.timelineStages?.map((stage, i) => (
                  <div key={i} className="flex items-center">
                    <span className="text-red-500 mr-3 text-xl">✓</span>
                    {stage.stage_name}
                  </div>
                ))}
              </div>

              <button
                onClick={handleStartGame}
                className="px-8 py-3 bg-[#D44D5C] text-white rounded-lg font-bold text-lg hover:bg-[#c03544] transition-colors shadow-lg shadow-red-200"
              >
                Start New Journey
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ============================================================================
  // RENDER: RESULTS SCREEN
  // ============================================================================

  if (gameStatus === 'results') {
    // Determine which outcome to show based on score
    const isSuccess = interestLevel >= 80;
    const isModerate = interestLevel >= 60 && interestLevel < 80;

    const outcome = isSuccess
      ? finalResults?.success_outcome
      : isModerate
        ? finalResults?.moderate_outcome
        : finalResults?.needs_work_outcome;

    if (!outcome) {
      return (
        <div className="h-full flex flex-col overflow-hidden">
          <div className="flex-1 flex items-center justify-center">
            <p className="text-red-500">Failed to load results</p>
          </div>
        </div>
      );
    }

    return (
      <div className="h-full flex flex-col overflow-hidden">
        {/* Fixed Header */}
        <div className="flex-shrink-0 pt-4 px-8">
          <div className="flex items-center gap-4 mb-4">
            <button
              onClick={onToggleSidebar}
              className="lg:hidden flex items-center justify-center w-10 h-10 hover:bg-gray-100 rounded transition-colors"
              aria-label="Toggle menu"
            >
              <IonIcon icon={hamIcon} size="small" color="medium" />
            </button>
            <h1 className="text-2xl font-bold">{course.gameTitle}</h1>
          </div>
        </div>

        {/* Scrollable Content */}
        <div className="flex-1 overflow-y-auto px-8 pb-8">

          <div id="lesson-progress">
            {/* Lesson Progress Indicator */}
            <div className="mb-3 flex items-center justify-between">
              <div>
                <span className="text-sm font-normal text-gray-600">STEP {stepNumber} OF {totalSteps}</span>
              </div>
              <div className="flex items-center">
                <div className={`mx-2 px-2 py-1.5 mt-1 text-xs font-medium rounded ${getStatusStyle()}`}>
                  {lessonStatus}
                </div>
                <div className="flex gap-0.5">
                  <IonButton
                    className="toolbar-button-icon-small"
                    fill="solid"
                    size="small"
                    color="medium"
                    onClick={(e) => handleNavigationClick('previous', e)}
                  >
                    <IonIcon icon={chevronBackOutline} className="fs-16" color="secondary" />
                  </IonButton>
                  <IonButton
                    className="toolbar-button-icon-small"
                    fill="solid"
                    size="small"
                    color="medium"
                    disabled={!hasNextLesson}
                    onClick={(e) => handleNavigationClick('next', e)}
                  >
                    <IonIcon icon={chevronForwardOutline} className="fs-16" color="secondary" />
                  </IonButton>
                </div>
              </div>
            </div>
          </div>

          <div className="max-w-5xl mx-auto animate-in fade-in duration-500">
            <div className={`rounded-2xl p-10 text-center border-2 ${isSuccess ? 'bg-green-50 border-green-200' : isModerate ? 'bg-yellow-50 border-yellow-200' : 'bg-red-50 border-red-200'}`}>
              <div className="text-6xl mb-6">{isSuccess ? '🎉' : isModerate ? '👍' : '⚠️'}</div>
              <h2 className={`text-4xl font-bold mb-4 ${isSuccess ? 'text-green-800' : isModerate ? 'text-yellow-800' : 'text-red-800'}`}>
                {outcome.section_1.title}
              </h2>
              <p className="text-xl text-gray-700 mb-8 max-w-2xl mx-auto">{outcome.section_1.outcome_summary}</p>

              <div className="text-6xl font-black text-gray-800 mb-2">{interestLevel}%</div>
              <div className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-8">{game.scoreMeter.label}</div>

              {/* Prospect Quote */}
              <div className="bg-white rounded-lg p-6 mb-8 text-left border-l-4 border-gray-300">
                <p className="text-gray-700 italic">"{outcome.section_1.prospect_quote}"</p>
                <p className="text-sm text-gray-500 mt-2">— {prospectProfile?.name}</p>
              </div>

              {/* Performance Breakdown */}
              <div className="text-left bg-white rounded-lg p-6 mb-8">
                <h3 className="text-xl font-bold text-gray-800 mb-4">{outcome.section_2.header}</h3>
                {outcome.section_2.items && outcome.section_2.items.map((item, idx) => (
                  <div key={idx} className="mb-4">
                    <h4 className="font-semibold text-gray-800">{item.title}</h4>
                    <p className="text-gray-600 text-sm">{item.description}</p>
                  </div>
                ))}
              </div>

              <div className="flex gap-4 justify-center">
                <button
                  onClick={handleStartGame}
                  className="px-8 py-3 bg-white border-2 border-gray-200 text-gray-700 rounded-lg font-bold hover:bg-gray-50 transition-colors"
                >
                  Replay Scenario
                </button>
                <button
                  onClick={progressToNext}
                  className={`px-8 py-3 rounded-lg font-bold transition-colors ${
                    isSuccess
                      ? 'bg-green-600 text-white hover:bg-green-700 shadow-lg shadow-green-200'
                      : isModerate
                        ? 'bg-yellow-600 text-white hover:bg-yellow-700 shadow-lg shadow-yellow-200'
                        : 'bg-gray-600 text-white hover:bg-gray-700 shadow-lg shadow-gray-200'
                  }`}
                >
                  {nextStepLabel}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ============================================================================
  // RENDER: PLAYING SCREEN
  // ============================================================================

  return (
    <div className="h-full flex flex-col overflow-hidden">
      {/* Fixed Header */}
      <div className="flex-shrink-0 pt-4 px-8">
        <div className="flex items-center gap-4 mb-4">
          <button
            onClick={onToggleSidebar}
            className="lg:hidden flex items-center justify-center w-10 h-10 hover:bg-gray-100 rounded transition-colors"
            aria-label="Toggle menu"
          >
            <IonIcon icon={hamIcon} size="small" color="medium" />
          </button>
          <h1 className="text-2xl font-bold">{course.gameTitle}</h1>
        </div>
      </div>

      {/* Scrollable Content */}
      <div className="flex-1 overflow-y-auto px-8 pb-8">

        <div id="lesson-progress">
          {/* Lesson Progress Indicator */}
          <div className="mb-3 flex items-center justify-between">
            <div>
              <span className="text-sm font-normal text-gray-600">STEP {stepNumber} OF {totalSteps}</span>
            </div>
            <div className="flex items-center">
              <div className={`mx-2 px-2 py-1.5 mt-1 text-xs font-medium rounded ${getStatusStyle()}`}>
                {lessonStatus}
              </div>
              <div className="flex gap-0.5">
                <IonButton
                  className="toolbar-button-icon-small"
                  fill="solid"
                  size="small"
                  color="medium"
                  onClick={(e) => handleNavigationClick('previous', e)}
                >
                  <IonIcon icon={chevronBackOutline} className="fs-16" color="secondary" />
                </IonButton>
                <IonButton
                  className="toolbar-button-icon-small"
                  fill="solid"
                  size="small"
                  color="medium"
                  disabled={!hasNextLesson}
                  onClick={(e) => handleNavigationClick('next', e)}
                >
                  <IonIcon icon={chevronForwardOutline} className="fs-16" color="secondary" />
                </IonButton>
              </div>
            </div>
          </div>

          {/* Progress Indicator */}
          {currentProgress && currentProgress.percentComplete < 100 && (
            <div className="mb-4 p-3 bg-gray-100 rounded-lg">
              <div className="flex justify-between items-center">
                <div className="text-sm font-medium text-gray-700">
                  <span className="hidden sm:inline">Progress: </span>{Math.round(currentProgress.percentComplete)}%
                </div>
                <div className="grow mx-4 h-2 bg-medium rounded-full overflow-hidden">
                  <div
                    className="w-full h-full bg-success rounded-full transition-all duration-300 ease-in-out"
                    style={{ width: `${Math.min(Math.max(Math.round(currentProgress.percentComplete), 0), 100)}%` }}
                  />
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="max-w-6xl mx-auto">

          {/* Profile Header */}
          {prospectProfile && (
            <div className="mb-6 bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
              {/* Banner */}
              <div className="w-full h-32 bg-gradient-to-r from-blue-200 to-indigo-100 rounded-t-2xl relative overflow-hidden mb-6">
                <img src={game.imageUrl} alt="Header" className="object-cover w-full h-full opacity-80" />
              </div>

              {/* Profile Bar */}
              <div className="p-6 pl-8 -mt-6 relative z-10 flex gap-6 items-start">
                {/* Avatar */}
                <div className="w-16 h-16 rounded-full border-4 border-white shadow-md overflow-hidden bg-gray-300 flex-shrink-0">
                  <img src={prospectProfile.imageUrl || "https://storage.googleapis.com/rev-public/default-avatar.png"} alt="" className="w-full h-full object-cover" />
                </div>

                {/* Info Container */}
                <div className="flex-1 min-w-0 flex flex-col md:flex-row gap-4 pt-2">
                  <div>
                    <div className="text-xs font-bold text-gray-500 uppercase">Name:</div>
                    <div className="text-xs font-semibold text-gray-800">{prospectProfile.name}</div>
                  </div>
                  <div>
                    <div className="text-xs font-bold text-gray-500 uppercase">Age:</div>
                    <div className="text-xs font-semibold text-gray-800">{prospectProfile.age}</div>
                  </div>
                  <div className="md:max-w-[150px]">
                    <div className="text-xs font-bold text-gray-500 uppercase">Occupation:</div>
                    <div className="text-xs font-semibold text-gray-800">{prospectProfile.occupation}</div>
                  </div>
                  <div className="md:flex-1">
                    <div className="text-xs font-bold text-gray-500 uppercase">Key Detail:</div>
                    <div className="text-xs font-semibold text-gray-800">{prospectProfile.key_detail}</div>
                  </div>
                  <div className="md:flex-1">
                    <div className="text-xs font-bold text-gray-500 uppercase">Mood:</div>
                    <div className="text-xs font-semibold text-gray-800">{prospectProfile.starting_mood}</div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Progress Dashboard */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 mb-6 flex flex-col md:flex-row items-center gap-8 relative overflow-hidden">
            {/* Left: Journey Title & Meter */}
            <div className="flex-1 w-full">
              <div className="mb-2">
                <div className="flex justify-between items-end mb-2">
                  <div>
                    <h2 className="text-2xl font-bold">{game.title}</h2>
                    <div className="text-lg font-bold text-gray-700 mt-1">{interestLevel}% {game.scoreMeter.label}</div>
                  </div>

                  {/* Step Circles (Visual Timeline) - Desktop only */}
                  <div className="hidden md:flex gap-4">
                    {game.timelineStages?.map(stage => (
                      <div
                        key={stage.stage_number}
                        className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold border-2 transition-all
                                 ${(currentScenarioIndex + 1) >= stage.stage_number
                            ? 'border-green-500 text-green-600 bg-green-50'
                            : 'border-gray-200 text-gray-700'
                          }
                                 ${(currentScenarioIndex + 1) === stage.stage_number ? 'ring-2 ring-green-200 ring-offset-2' : ''}
                              `}
                      >
                        {stage.stage_number}
                      </div>
                    ))}
                  </div>
                </div>

                {/* Step Circles (Visual Timeline) - Mobile: above progress bar, smaller */}
                <div className="md:hidden flex gap-2 mb-2">
                  {game.timelineStages?.map(stage => (
                    <div
                      key={stage.stage_number}
                      className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold border-2 transition-all
                               ${(currentScenarioIndex + 1) >= stage.stage_number
                          ? 'border-green-500 text-green-600 bg-green-50'
                          : 'border-gray-200 text-gray-700'
                        }
                               ${(currentScenarioIndex + 1) === stage.stage_number ? 'ring-2 ring-green-200 ring-offset-1' : ''}
                            `}
                    >
                      {stage.stage_number}
                    </div>
                  ))}
                </div>
              </div>

              {/* Gradient Bar */}
              <div className="h-4 w-full bg-gray-100 rounded-full overflow-hidden mt-3 relative">
                <div
                  className="absolute top-0 left-0 h-full bg-gradient-to-r from-red-500 via-yellow-400 to-green-500 transition-all duration-700 ease-out"
                  style={{ width: `${interestLevel}%` }}
                />
              </div>
            </div>

            {/* Right: Icon */}
            <div className="hidden lg:flex w-16 h-16 bg-gradient-to-br from-pink-500 to-purple-600 rounded-2xl shadow-lg items-center justify-center text-white text-2xl transform rotate-3 hover:rotate-0 transition-transform cursor-pointer">
              📱
            </div>
          </div>

          {/* Main Gameplay Area */}
          {currentScenario && (
            <div className="bg-white rounded-xl shadow-md border border-gray-100 overflow-hidden min-h-[400px]">

              {/* Scenario Header */}
              <div className="bg-gray-50 border-b border-gray-100 px-8 py-4">
                <span className="bg-pink-100 text-[#D44D5C] px-3 py-1 rounded text-xs font-bold uppercase tracking-wider">
                  {currentScenario.type}
                </span>
              </div>

              <div className="p-8">
                <h3 className="font-semibold mb-6 leading-relaxed">
                  {currentScenario.scenarioText}
                </h3>

                {/* Choices Grid */}
                <div className="space-y-4">
                  {currentScenario.choices.map((choice, idx) => {
                    const isSelected = selectedChoiceIndex === idx;
                    const isDisabled = showFeedback && !isSelected;

                    // Quality level based colors
                    const getQualityStyles = () => {
                      if (!isSelected) return { border: 'border-gray-200', bg: 'bg-white', ring: '', radioBorder: 'border-gray-300', radioBg: 'bg-transparent' };

                      switch(choice.qualityLevel) {
                        case 'Excellent':
                          return { border: 'border-green-700', bg: 'bg-green-50', ring: 'ring-4 ring-green-100', radioBorder: 'border-green-700', radioBg: 'bg-green-700' };
                        case 'Good':
                          return { border: 'border-green-400', bg: 'bg-green-50', ring: 'ring-4 ring-green-100', radioBorder: 'border-green-400', radioBg: 'bg-green-400' };
                        case 'Poor':
                          return { border: 'border-yellow-400', bg: 'bg-yellow-50', ring: 'ring-4 ring-yellow-100', radioBorder: 'border-yellow-500', radioBg: 'bg-yellow-500' };
                        case 'Dangerous':
                          return { border: 'border-red-500', bg: 'bg-red-50', ring: 'ring-4 ring-red-100', radioBorder: 'border-red-500', radioBg: 'bg-red-500' };
                        default:
                          return { border: 'border-gray-200', bg: 'bg-white', ring: '', radioBorder: 'border-gray-300', radioBg: 'bg-transparent' };
                      }
                    };

                    const styles = getQualityStyles();

                    return (
                      <button
                        key={idx}
                        onClick={() => handleSelectChoice(idx)}
                        disabled={showFeedback}
                        className={`w-full text-left p-2 rounded-xl border-2 transition-all duration-300 flex items-start group
                                 ${isSelected
                            ? `${styles.border} ${styles.bg} ${styles.ring}`
                            : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                          }
                                 ${isDisabled ? 'opacity-40 grayscale' : 'opacity-100'}
                              `}
                      >
                        {/* Radio Indicator */}
                        <div className={`w-6 h-6 rounded-full border-2 mr-4 mt-1 flex-shrink-0 flex items-center justify-center transition-colors
                                 ${isSelected ? `${styles.radioBorder} ${styles.radioBg}` : 'border-gray-300 group-hover:border-gray-400'}
                              `}>
                          {isSelected && <div className="w-2.5 h-2.5 bg-white rounded-full" />}
                        </div>

                        <span className={`${isSelected ? 'text-gray-900 font-medium' : 'text-gray-900'}`}>
                          {choice.text}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Feedback Overlay */}
              {showFeedback && selectedChoiceIndex !== null && (() => {
                const selectedQuality = currentScenario.choices[selectedChoiceIndex].qualityLevel;
                const feedbackStyles = {
                  Excellent: { bg: 'bg-green-50', border: 'border-green-400', title: 'text-green-800', body: 'text-green-900' },
                  Good: { bg: 'bg-green-50', border: 'border-green-400', title: 'text-green-800', body: 'text-green-900' },
                  Poor: { bg: 'bg-yellow-100', border: 'border-yellow-500', title: 'text-yellow-900', body: 'text-yellow-900' },
                  Dangerous: { bg: 'bg-red-50', border: 'border-red-500', title: 'text-red-900', body: 'text-red-900' },
                }[selectedQuality] ?? { bg: 'bg-gray-50', border: 'border-gray-400', title: 'text-gray-800', body: 'text-gray-900' };
                return (
                  <div className={`${feedbackStyles.bg} border-t-2 ${feedbackStyles.border} p-8 animate-in slide-in-from-bottom-10 fade-in duration-500`}>
                    <div className="flex items-start gap-4">
                      <div className="text-4xl">
                        {selectedQuality === 'Excellent' ? '🌟' :
                          selectedQuality === 'Poor' ? '⚠️' :
                          selectedQuality === 'Dangerous' ? '🚨' : '🤔'}
                      </div>
                      <div className="flex-1">
                        <h4 className={`text-xl font-bold ${feedbackStyles.title} mb-2`}>
                          {currentScenario.choices[selectedChoiceIndex].feedbackTitle}
                        </h4>
                        <p className={`${feedbackStyles.body} leading-relaxed mb-6`}>
                          {currentScenario.choices[selectedChoiceIndex].feedbackText}
                        </p>

                        <div className="flex justify-end">
                          <button
                            onClick={handleNextScenario}
                            className="bg-[#D44D5C] text-white px-8 py-3 rounded-lg font-bold shadow-md hover:bg-[#c03544] hover:shadow-lg transition-all transform hover:-translate-y-0.5 active:translate-y-0"
                          >
                            {currentScenarioIndex === scenarios.length - 1 ? 'View Results' : 'Next Scenario'}
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })()}
            </div>
          )}

        </div>
      </div>

      {/* Navigation Confirmation Popover */}
      <PopoverMenuComponent
        isOpen={showNavigationPopover}
        event={navigationPopoverEvent}
        onDismiss={handlePopoverDismiss}
        menuItems={[
          {
            key: 'confirm',
            value: `Click to confirm you want to leave and go to the ${navigationDirection === 'next' ? 'next' : 'previous'} step.`
          }
        ]}
      />
    </div>
  );
};
